import React, { FC } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useCabinetTenderList, useCabinetLotList } from '@web-kc/core/api';
import { CPagination, CTabs } from '@web-kc/shared/ui';
import { statusTabs, createTypeTabs } from './constants';
import { useTranslation } from 'next-i18next';
import { TenderList } from '../components/Cabinet/TenderList';
import { LotList } from '../components/Cabinet/LotList';
import { TenderHeader } from '../components/Cabinet/TenderHeader';
import { useRouter } from 'next/router';
import { useFiltersQuery } from '../PublicTenders/parser';
import LotAndTenderSearchFiltersRefactor from '../components/LotAndTenderSearchFiltersRefactor';

interface Props {
  type: 'lots' | 'tenders';
}

export const MyTenders: FC<Props> = ({ type }) => {
  const { query, push } = useRouter();
  const { t } = useTranslation(['cabinet', 'common']);

  const [searchFilters, setSearchFilters] = useFiltersQuery({
    initiator: 'ME',
    page: 0,
    size: 10,
    sort: 'CREATED_DESC',
  });

  const { data: lots, isLoading: isLoadingLots } = useCabinetLotList(
    searchFilters as any,
    type === 'lots',
  );
  const { data: tenders, isLoading: isLoadingTenders } = useCabinetTenderList(
    searchFilters as any,
    type === 'tenders',
  );

  const makeTabId = (arr: string[]) =>
    arr.length ? [...arr].sort().join('|') : '__ALL__';
  const asSet = (arr?: string[]) => new Set(arr ?? []);
  const setsEqual = (a?: string[], b?: string[]) => {
    if (!a?.length && !b?.length) return true; // обе пустые → равны (это и есть ALL)
    const A = asSet(a);
    const B = asSet(b);
    if (A.size !== B.size) return false;
    for (const v of A) if (!B.has(v)) return false;
    return true;
  };

  const statusTabsUi = statusTabs.map(({ title, key }) => ({
    id: makeTabId(key),
    statuses: key,
    title: t(`cabinet:${title}`),
  }));

  const allTabId = makeTabId([]);

  const activeStatusTabId = (() => {
    const current = Array.isArray(searchFilters.status)
      ? searchFilters.status
      : [];
    const exact = statusTabsUi.find((tab) => setsEqual(current, tab.statuses));
    return exact ? exact.id : allTabId;
  })();

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={1.25}
          sx={{ overflowX: 'auto' }}
        >
          <CTabs
            size="small"
            tabs={createTypeTabs.map(({ title, key }) => ({
              key,
              title: t(`cabinet:${title}`),
            }))}
            tabLabelKey="title"
            tabValueKey="key"
            value={`${searchFilters.initiator}`}
            onChange={(tab) =>
              setSearchFilters({ ...searchFilters, initiator: tab as 'ME' })
            }
          />
          <Box
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              '-ms-overflow-style': 'none',
              'scrollbar-width': 'none',
            }}
          >
            <CTabs
              size="small"
              tabLabelKey="title"
              tabValueKey="id"
              tabs={statusTabsUi}
              value={activeStatusTabId}
              onChange={(tabId) => {
                const picked = statusTabsUi.find((t) => t.id === tabId);
                const nextStatuses = picked ? picked.statuses : [];
                setSearchFilters({
                  ...searchFilters,
                  status: nextStatuses,
                  page: 0,
                });
              }}
            />
          </Box>
        </Stack>
      </Stack>

      <LotAndTenderSearchFiltersRefactor
        initialFilters={searchFilters}
        onApply={(newFilters) => {
          setSearchFilters(
            { ...newFilters, initiator: searchFilters.initiator },
            { replace: true },
          );
        }}
      />
      <Paper
        elevation={1}
        sx={{
          backgroundColor: { xs: 'transparent', md: 'white' },
          borderRadius: '4px',
          mt: { xs: 1.5 },
          padding: { xs: 0, md: '20px' },
        }}
      >
        <TenderHeader
          tab={type}
          onTabChange={(nextType) => {
            if (nextType !== type) {
              push({ pathname: `/cabinet/${nextType}`, query });
            }
          }}
        />

        <Box>
          {type === 'lots' ? (
            <LotList data={lots} isLoading={isLoadingLots} />
          ) : (
            <TenderList data={tenders} isLoading={isLoadingTenders} />
          )}
        </Box>
      </Paper>

      {Number((type === 'lots' ? lots?.pages : tenders?.pages) ?? 0) > 1 && (
        <Box mt={2.5}>
          <CPagination
            isTextShown={false}
            count={Number(type === 'lots' ? lots?.pages : tenders?.pages)}
            currentPage={
              searchFilters.page ? Number(searchFilters.page) + 1 : 1
            }
            handlePageChange={(page) =>
              setSearchFilters({
                ...searchFilters,
                page: page - 1,
              })
            }
          />
        </Box>
      )}
    </Box>
  );
};
